import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '@/shared/ui/cn';
import type { IconName } from '@/shared/ui/Icon';
import { Icon } from '@/shared/ui/Icon';
import { Spinner } from '@/shared/ui/Spinner';

/**
 * `primary` est la seule couleur d'action : un seul bouton `primary` par écran,
 * celui qui porte ce que l'utilisateur est venu accomplir. Tout le reste est
 * `secondary`, `outline` ou `quiet`.
 *
 * `success` ne sert qu'à confirmer un encaissement ou une réception. `danger`
 * est du texte rouge, jamais un aplat plein. `stage` habille une commande posée
 * sur une vidéo (le fil plein écran, le direct).
 *
 * `inactive` : le formulaire n'est pas prêt (un champ requis manque). Le bouton
 * se présente en retrait, comme éteint, mais répond toujours : le clic dit ce
 * qui manque (`useFormGuard`), et le bouton reprend sa couleur dès que tout est
 * rempli. Jamais `disabled` pour cela : un bouton muet passe pour une panne.
 */
export const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap select-none',
    'text-button transition-[background-color,color,box-shadow,opacity] duration-150 ease-settle',
    'disabled:pointer-events-none aria-disabled:pointer-events-none',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-fg-on-primary hover:bg-primary-hover active:bg-primary-pressed disabled:bg-fill disabled:text-fg-faint',
        secondary: 'bg-fill text-fg hover:bg-fill-hover active:opacity-80 disabled:text-fg-faint',
        outline:
          'bg-transparent text-fg shadow-[inset_0_0_0_1px_var(--bul-line)] hover:bg-sunken active:bg-fill disabled:text-fg-faint',
        quiet:
          'bg-transparent text-fg-muted hover:bg-sunken hover:text-fg active:bg-fill disabled:opacity-50',
        ink: 'bg-fg text-bg hover:opacity-90 active:opacity-80 disabled:bg-fill disabled:text-fg-faint',
        success:
          'bg-success text-fg-on-primary hover:bg-success-pressed disabled:bg-fill disabled:text-fg-faint',
        danger:
          'bg-transparent text-danger hover:bg-danger-soft active:opacity-80 disabled:opacity-50',
        link: 'h-auto bg-transparent px-0 text-primary-text hover:underline disabled:opacity-50',
        stage: 'bg-stage-fill text-stage-fg backdrop-blur-md hover:bg-white/25 disabled:opacity-50',
      },
      size: {
        xs: 'h-[30px] rounded-[7px] px-[11px] text-caption font-medium',
        sm: 'h-9 rounded-sm px-3.5 text-subtle font-medium',
        md: 'h-11 rounded-sm px-[18px]',
        lg: 'h-12 rounded-sm px-[18px] text-body font-medium',
      },
      block: {
        true: 'w-full',
        false: '',
      },
      inactive: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { variant: 'link', className: 'h-auto px-0' },
      // En retrait : la mine du bouton désactivé, sans en être un.
      {
        variant: 'primary',
        inactive: true,
        className: 'bg-fill text-fg-faint hover:bg-fill active:bg-fill',
      },
      {
        variant: 'secondary',
        inactive: true,
        className: 'text-fg-faint hover:bg-fill active:opacity-100',
      },
      {
        variant: 'outline',
        inactive: true,
        className: 'text-fg-faint hover:bg-transparent active:bg-transparent',
      },
      {
        variant: 'ink',
        inactive: true,
        className: 'bg-fill text-fg-faint hover:opacity-100 active:opacity-100',
      },
      {
        variant: 'success',
        inactive: true,
        className: 'bg-fill text-fg-faint hover:bg-fill',
      },
      { variant: 'quiet', inactive: true, className: 'opacity-50' },
      { variant: 'danger', inactive: true, className: 'opacity-50 hover:bg-transparent' },
      { variant: 'link', inactive: true, className: 'opacity-50' },
      { variant: 'stage', inactive: true, className: 'opacity-50 hover:bg-stage-fill' },
    ],
    defaultVariants: { variant: 'secondary', size: 'md', block: false, inactive: false },
  },
);

const ICON_SIZE = { xs: 14, sm: 16, md: 18, lg: 18 } as const;

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    icon?: IconName;
    iconRight?: IconName;
    loading?: boolean;
    /** Rend l'enfant (un lien) avec le style du bouton, au lieu d'un `<button>`. */
    asChild?: boolean;
    ref?: Ref<HTMLButtonElement>;
    children?: ReactNode;
  };

export function Button({
  variant,
  size,
  block,
  inactive = false,
  icon,
  iconRight,
  loading = false,
  disabled,
  asChild = false,
  className,
  children,
  type,
  ref,
  ...rest
}: ButtonProps) {
  const iconSize = ICON_SIZE[size ?? 'md'];
  const classes = cn(buttonVariants({ variant, size, block, inactive }), className);

  // Un lien habillé en bouton garde ses icônes : `Slottable` pose l'enfant (le
  // lien) comme racine, et les icônes de part et d'autre de son texte.
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} data-inactive={inactive || undefined} {...rest}>
        {icon ? <Icon name={icon} size={iconSize} /> : null}
        <Slot.Slottable>{children}</Slot.Slottable>
        {iconRight ? <Icon name={iconRight} size={iconSize} /> : null}
      </Slot.Root>
    );
  }

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      data-inactive={inactive || undefined}
      {...rest}
    >
      {loading ? <Spinner size={iconSize} /> : icon ? <Icon name={icon} size={iconSize} /> : null}
      {children !== undefined && children !== null ? (
        <span className={cn('truncate', loading && 'opacity-80')}>{children}</span>
      ) : null}
      {iconRight && !loading ? <Icon name={iconRight} size={iconSize} /> : null}
    </button>
  );
}
